/**
 * Automatic free-gift-with-purchase reconciler (UX layer).
 *
 * Keeps exactly one free-gift line (quantity 1, carrying the hidden `_free_gift`
 * line-item property) in the cart whenever the qualifying subtotal reaches the
 * configured threshold, and removes it when the cart drops below the threshold.
 *
 * IMPORTANT: this is presentation only. The actual 100%-off is applied server-side by
 * the Discount Function (which is the source of truth and also runs at checkout). If the
 * Function/discount is not live, `window.CartIncentives.freeGift.enabled` will be false
 * and this script does nothing, so a full-price "free" line is never created.
 *
 * The `_free_gift` property keeps the free unit on its own line (Shopify only merges
 * lines with identical variant AND properties), so the shopper's own paid units of the
 * same product stay separate — the gift is additive.
 */
(function () {
  var incentives = window.CartIncentives || {};
  var cfg = incentives.freeGift || {};
  var routes = incentives.routes || {};

  if (!cfg.enabled || !cfg.giftVariantId || !cfg.thresholdCents) return;

  var GIFT_PROPERTY = '_free_gift';
  var giftVariantNumericId = String(cfg.giftVariantId).split('/').pop();
  var cartRoute = routes.cart || '/cart';
  var addRoute = routes.cartAdd || '/cart/add';
  var changeRoute = routes.cartChange || '/cart/change';
  var reconciling = false;

  function getCart() {
    // `cache: 'no-store'` is essential: on return-from-checkout navigations the browser can
    // otherwise serve a cached /cart.js snapshot taken BEFORE the gift line was added, which
    // makes us think the gift is missing and add a duplicate.
    return fetch(cartRoute + '.js', {
      headers: { Accept: 'application/json' },
      cache: 'no-store'
    }).then(function (r) {
      return r.json();
    });
  }

  function qualifyingSubtotal(cart) {
    var field = cfg.subtotalBasis === 'post_discount' ? 'final_line_price' : 'original_line_price';
    return cart.items.reduce(function (sum, item) {
      var isGift = item.properties && item.properties[GIFT_PROPERTY];
      return isGift ? sum : sum + item[field];
    }, 0);
  }

  // Returns EVERY line that is one of our free-gift units (the gift variant carrying the
  // hidden `_free_gift` property). There should only ever be one, but returning all of them
  // lets reconcile() self-heal a duplicated gift line. Untagged lines of the same variant are
  // deliberately excluded — those are the shopper's own paid units and must never be touched.
  function findGiftLines(cart) {
    return cart.items.filter(function (item) {
      return (
        item.properties &&
        item.properties[GIFT_PROPERTY] &&
        String(item.id) === String(giftVariantNumericId)
      );
    });
  }

  // Sets an arbitrary number of lines to quantity 0, one at a time. Line `key`s stay valid
  // across changes, so sequential calls are safe (parallel ones can race the cart lock).
  function zeroLines(lines) {
    return lines.reduce(function (chain, line) {
      return chain.then(function () {
        return setGiftQuantity(line, 0);
      });
    }, Promise.resolve());
  }

  function addGift() {
    var body = { id: Number(giftVariantNumericId), quantity: 1, properties: {} };
    body.properties[GIFT_PROPERTY] = 'true';
    return fetch(addRoute + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    });
  }

  function setGiftQuantity(line, quantity) {
    return fetch(changeRoute + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: line.key, quantity: quantity })
    });
  }

  function refreshCartUI() {
    document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh'));
    var cartItems = document.querySelector('cart-items');
    if (cartItems && typeof cartItems.refresh === 'function') cartItems.refresh();
  }

  function reconcile() {
    if (reconciling) return Promise.resolve();
    reconciling = true;
    var changed = false;

    return getCart()
      .then(function (cart) {
        var giftLines = findGiftLines(cart);
        var qualifies = qualifyingSubtotal(cart) >= cfg.thresholdCents;

        // Below the threshold: remove every free-gift line.
        if (!qualifies) {
          if (giftLines.length) {
            changed = true;
            return zeroLines(giftLines);
          }
          return null;
        }

        // Qualifies but no gift yet: add exactly one.
        if (giftLines.length === 0) {
          changed = true;
          return addGift();
        }

        // Qualifies and at least one gift line exists. Keep the first, drop any duplicates
        // (self-heals a doubled-up gift), then make sure the survivor is a single free unit.
        var keep = giftLines[0];
        var extras = giftLines.slice(1);
        if (extras.length) {
          changed = true;
          return zeroLines(extras).then(function () {
            return keep.quantity > 1 ? setGiftQuantity(keep, 1) : null;
          });
        }
        // Shopper bumped the free line above 1 — force it back to a single free unit.
        if (keep.quantity > 1) {
          changed = true;
          return setGiftQuantity(keep, 1);
        }
        return null;
      })
      .catch(function (error) {
        console.log('[free-gift] reconcile failed', error); // eslint-disable-line no-console
      })
      .then(function () {
        reconciling = false;
        // Our own mutations use raw /cart/*.js calls that do NOT emit the theme's cart
        // events, so refreshing here can't re-trigger this reconciler.
        if (changed) refreshCartUI();
      });
  }

  function debounce(fn, wait) {
    var timer;
    return function () {
      clearTimeout(timer);
      timer = setTimeout(fn, wait);
    };
  }

  var debouncedReconcile = debounce(reconcile, 400);

  // Fires on add-to-cart for a new variant and on any line-item quantity change/removal.
  // The theme only emits on:cart:add when the variant was NOT already present, so we must
  // listen to both to catch every cart mutation.
  document.addEventListener('on:cart:add', debouncedReconcile);
  document.addEventListener('on:line-item:change', debouncedReconcile);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', reconcile);
  } else {
    reconcile();
  }
})();
